// Cross-engine text scans over the DDL statement the caret sits in — the two
// answers a schema snapshot cannot give, because the objects in question do
// not exist yet (an in-progress CREATE TABLE body) or are named AFTER the
// caret (a trigger's `ON <tbl>`).
//
// Both scans back slots the DDL unknown-position sweep routed for the first
// time, and both are needed by more than one engine, so they live here rather
// than in a resolver:
//   - `extractInProgressCreateTableColumns` — `ddl-create-table-expression`
//     (pgsql script.29 §6 + 42 §3, mysql script.44 §2). sqlite / mysql / mssql
//     each grew a private copy of this shape before it was needed a fourth
//     time; new callers use this one.
//   - `findTriggerOnTableAhead` — `ddl-target-table-column`
//     (sqlite script.33 §2, oracle script.36 §2). The `UPDATE OF <cols>` list
//     precedes the `ON <tbl>` clause that owns it, so the ONLY way to bind the
//     table is to read past the caret.

/** One column declared in an in-progress CREATE TABLE body. */
export type InProgressColumn = {
  readonly name: string;
  /**
   * True for a `GENERATED ALWAYS AS (…)` / `AS (…) STORED` computed column. A
   * generation expression may not reference another generated column, so the
   * `GENERATED AS (▮` slot filters these out (mysql script.44 §2).
   */
  readonly generated: boolean;
};

// `CREATE [OR REPLACE] [TEMP|TEMPORARY|GLOBAL|LOCAL] TABLE [IF NOT EXISTS]
// <dotted name> (` — the column-list opener. Identifier forms cover every
// dialect's quoting (`"x"`, `[x]`, `` `x` ``, bare).
const IDENT = '(?:"(?:[^"]|"")*"|\\[[^\\]]*\\]|`(?:[^`]|``)*`|[A-Za-z_][\\w$]*)';
const CREATE_TABLE_HEAD = new RegExp(
  `\\bCREATE\\s+(?:OR\\s+REPLACE\\s+)?(?:(?:GLOBAL|LOCAL)\\s+)?(?:TEMP(?:ORARY)?\\s+)?TABLE\\s+` +
    `(?:IF\\s+NOT\\s+EXISTS\\s+)?${IDENT}(?:\\s*\\.\\s*${IDENT})?\\s*\\(`,
  'i',
);
const FIRST_IDENT = new RegExp(
  `^\\s*(?:"((?:[^"]|"")*)"|\\[([^\\]]*)\\]|\`((?:[^\`]|\`\`)*)\`|([A-Za-z_][\\w$]*))`,
);

// A body segment that LEADS with one of these declares a table-level
// constraint, not a column, so it contributes no name.
const TABLE_CONSTRAINT_LEAD: ReadonlySet<string> = new Set([
  'primary', 'unique', 'foreign', 'constraint', 'check', 'exclude', 'index',
  'key', 'fulltext', 'spatial', 'period',
]);

/**
 * The column names declared SO FAR in the CREATE TABLE statement the caret is
 * inside, in declaration order, deduped case-insensitively.
 *
 * Walks to the `CREATE TABLE … (` opener, then splits the body up to the
 * caret on TOP-LEVEL commas. Paren depth is tracked, so the still-open
 * `CHECK (` / `PRIMARY KEY (` the caret sits in never terminates a segment and
 * the partially-typed final segment is included — its own first identifier is
 * either the column being declared or a constraint lead that is skipped.
 *
 * Returns `[]` when the caret is not inside a CREATE TABLE body.
 */
export function extractInProgressCreateTableColumns(
  text: string,
  statementStart: number,
  cursorOffset: number,
): readonly InProgressColumn[] {
  const slice = text.slice(statementStart, cursorOffset);
  const head = CREATE_TABLE_HEAD.exec(slice);
  if (!head) return [];
  const body = slice.slice(head.index + head[0].length);

  const segments: string[] = [];
  let depth = 0;
  let segStart = 0;
  for (let i = 0; i < body.length; i += 1) {
    const ch = body[i]!;
    if (ch === '(') depth += 1;
    else if (ch === ')') depth -= 1;
    else if (ch === ',' && depth === 0) {
      segments.push(body.slice(segStart, i));
      segStart = i + 1;
    }
  }
  segments.push(body.slice(segStart));

  const out: InProgressColumn[] = [];
  const seen = new Set<string>();
  for (const seg of segments) {
    const m = FIRST_IDENT.exec(seg);
    if (!m) continue;
    const raw = m[1] ?? m[2] ?? m[3] ?? m[4] ?? '';
    if (!raw) continue;
    const key = raw.toLowerCase();
    if (TABLE_CONSTRAINT_LEAD.has(key)) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ name: raw, generated: /\bGENERATED\b|\bAS\s*\(/i.test(seg) });
  }
  return out;
}

/**
 * True when the caret sits inside a `CREATE TABLE` / `ALTER TABLE` statement.
 *
 * Used to switch OFF the MetaInfo value hints (enum caches, JSON paths). Those
 * hints resolve their column reference through
 * `resolveColumnRefToTable`, whose documented fallback for a BARE reference is
 * "the first snapshot table that owns a column of that name". In a query that
 * ambiguity is acceptable — the reference does name a column of some relation
 * the statement pulled in. In table DDL there is no queried relation at all:
 * `CREATE TABLE t (status …, CHECK (status IN (▮)))` names a column of the
 * table being DEFINED, which the snapshot cannot know yet, so the fallback
 * silently answered with an UNRELATED table's cached values (oracle coverage
 * script.29 §4: the `ORDERS.STATUS` cache leaking into a fresh `status`
 * column). No table-DDL caret has a MetaInfo hint to offer, so the whole
 * statement class is excluded rather than the one shape.
 *
 * Cross-engine by construction (all five spell these two heads the same) and
 * called from every engine's enum collector.
 */
export function isInsideTableDdlStatement(text: string, cursorOffset: number): boolean {
  // The caret's own statement, approximated by the last `;` before it. The
  // callers (per-engine enum collectors) receive no statement range, and the
  // approximation is safe in the only direction that matters: a `;` inside a
  // literal moves the start FORWARD, so the slice no longer begins with a DDL
  // head and the predicate declines.
  const start = text.lastIndexOf(';', Math.max(0, cursorOffset - 1)) + 1;
  const slice = text.slice(start, Math.max(0, cursorOffset));
  if (!TABLE_DDL_HEAD.test(slice)) return false;
  // `CREATE TABLE t AS SELECT … WHERE status = '▮'` (CTAS) is a QUERY wearing
  // a DDL head: the caret sits in a real WHERE over real relations, so the
  // hints belong there. Any SELECT before the caret means a query is in play.
  return !/\bSELECT\b/i.test(slice);
}

const TABLE_DDL_HEAD =
  /^\s*(?:CREATE\s+(?:OR\s+REPLACE\s+)?(?:(?:GLOBAL|LOCAL|PRIVATE)\s+)?(?:TEMP(?:ORARY)?\s+)?(?:UNLOGGED\s+)?TABLE|ALTER\s+TABLE)\b/i;

/**
 * The table a `CREATE TRIGGER` fires on, read from the `ON <tbl>` clause that
 * follows the caret. Used by the `UPDATE OF ▮` column list, whose owning table
 * is not knowable from the text before the caret.
 *
 * `statementEnd` bounds the scan to the caret's own statement so a later
 * statement's `ON` can never be picked up. Parts come back UNQUOTED but in
 * their written CASE (`schema` / `name`), and also as WRITTEN, delimiters
 * included (`schemaToken` / `nameToken`) — identifier folding differs per
 * dialect (Oracle folds up, the rest down; PostgreSQL and Oracle keep a
 * delimited part's case), so each caller applies its own `foldIdentifier`.
 */
export function findTriggerOnTableAhead(
  text: string,
  cursorOffset: number,
  statementEnd: number,
): { schema: string | null; name: string; schemaToken: string | null; nameToken: string } | null {
  const ahead = text.slice(cursorOffset, Math.max(cursorOffset, statementEnd));
  const m = new RegExp(`\\bON\\s+(?:(${IDENT})\\s*\\.\\s*)?(${IDENT})`, 'i').exec(ahead);
  if (!m || !m[2]) return null;
  return {
    schema: m[1] ? stripQuotes(m[1]) : null,
    name: stripQuotes(m[2]),
    schemaToken: m[1] ?? null,
    nameToken: m[2],
  };
}

/**
 * The SOURCE table an fts5 virtual table mirrors, read from the
 * `content='<tbl>'` option that follows the caret inside the module argument
 * list — `CREATE VIRTUAL TABLE t USING fts5(▮, Composer, content='Track')`.
 *
 * An external-content fts5 table names its columns after the real table's, so
 * that table's column list is the answer at the argument slot. When the option
 * is absent the table is self-contained and there is nothing to suggest: the
 * function returns `null` and the caller emits nothing rather than flooding.
 *
 * Returned UNQUOTED and in its written case; the caller folds.
 */
export function findFtsContentTableAhead(
  text: string,
  cursorOffset: number,
  statementEnd: number,
): string | null {
  const ahead = text.slice(cursorOffset, Math.max(cursorOffset, statementEnd));
  const m = /\bcontent\s*=\s*(?:'([^']*)'|"([^"]*)")/i.exec(ahead);
  const name = m?.[1] ?? m?.[2];
  return name ? name : null;
}

/**
 * True when the caret's innermost unclosed `(` was opened by a COMPUTED-column
 * generation expression — `GENERATED ALWAYS AS (▮` or MySQL's shorter
 * `<col> <type> AS (▮`. Callers use it to drop the generated columns from the
 * in-progress list: no dialect lets one generated column reference another,
 * and the column being defined cannot reference itself.
 *
 * Only meaningful inside a CREATE/ALTER TABLE body, which every caller has
 * already established (they reach this from the
 * `ddl-create-table-expression` slot), so the bare `AS (` form cannot be
 * confused with a CTE's or a view's.
 */
export function isInsideGenerationExpression(
  text: string,
  statementStart: number,
  cursorOffset: number,
): boolean {
  let depth = 0;
  let openIdx = -1;
  for (let i = cursorOffset - 1; i >= statementStart; i -= 1) {
    const ch = text[i]!;
    if (ch === ')') depth += 1;
    else if (ch === '(') {
      if (depth === 0) {
        openIdx = i;
        break;
      }
      depth -= 1;
    }
  }
  if (openIdx < 0) return false;
  const before = text.slice(Math.max(statementStart, openIdx - 64), openIdx);
  return /\b(?:GENERATED\s+(?:ALWAYS|BY\s+DEFAULT)\s+)?AS\s*$/i.test(before);
}

/**
 * Column names added to `table` by `ALTER TABLE … ADD [COLUMN] <name>`
 * statements EARLIER IN THE BUFFER than the caret. The schema snapshot is
 * built from the live database, so a column the script itself adds a few lines
 * up is invisible to it — and at an `ALTER TABLE … RENAME COLUMN ▮` caret that
 * column is exactly the one the user is most likely reaching for
 * (sqlite script.35 §4).
 *
 * Deliberately buffer-wide (not statement-scoped): the ADD COLUMN lives in a
 * PREVIOUS statement by construction. The caller supplies its own `fold` (the
 * dialect's identifier folding — Oracle folds up, the rest down) plus the
 * already-folded table it is emitting for.
 */
export function extractInBufferAddedColumns(
  text: string,
  cursorOffset: number,
  table: {
    readonly schema: string | null;
    readonly name: string;
    readonly fold: (raw: string) => string;
  },
): readonly string[] {
  const before = text.slice(0, cursorOffset);
  const re = new RegExp(
    `\\bALTER\\s+TABLE\\s+(?:(${IDENT})\\s*\\.\\s*)?(${IDENT})\\s+ADD\\s+(?:COLUMN\\s+)?(${IDENT})`,
    'gi',
  );
  const out: string[] = [];
  const seen = new Set<string>();
  let m: RegExpExecArray | null = null;
  while ((m = re.exec(before)) !== null) {
    if (table.fold(stripQuotes(m[2]!)) !== table.name) continue;
    // A qualified ALTER must target the same schema; an unqualified one is
    // taken as the caller's table (single-namespace engines and the common
    // default-schema case).
    if (m[1] && table.schema && table.fold(stripQuotes(m[1])) !== table.schema) continue;
    const raw = stripQuotes(m[3]!);
    const key = table.fold(raw);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(raw);
  }
  return out;
}

/** One routine created earlier in the same buffer. */
export type InBufferRoutine = {
  readonly schema: string | null;
  readonly name: string;
  readonly kind: 'procedure' | 'function';
};

/**
 * Routines whose `CREATE [OR ALTER | OR REPLACE] PROCEDURE|PROC|FUNCTION`
 * statement lives earlier in the buffer, before the caret.
 *
 * Same rationale as the in-buffer CREATE TABLE / SEQUENCE scans: a script that
 * defines a procedure and then EXECs it a few lines down is the normal
 * authoring flow, and the snapshot cannot know the routine until the DDL has
 * actually run — so `EXEC dbo.▮` offered every OTHER procedure but not the one
 * the script just wrote (mssql script.43 §11). Cross-engine by construction:
 * the identifier pattern covers every dialect's quoting and both the T-SQL
 * (`OR ALTER`) and PG/Oracle (`OR REPLACE`) spellings.
 */
export function scanBufferCreatedRoutines(
  text: string,
  cursorOffset: number,
): readonly InBufferRoutine[] {
  const before = text.slice(0, cursorOffset);
  const re = new RegExp(
    `\\bCREATE\\s+(?:OR\\s+(?:ALTER|REPLACE)\\s+)?(PROCEDURE|PROC|FUNCTION)\\s+` +
      `(?:(${IDENT})\\s*\\.\\s*)?(${IDENT})`,
    'gi',
  );
  const out: InBufferRoutine[] = [];
  const seen = new Set<string>();
  let m: RegExpExecArray | null = null;
  while ((m = re.exec(before)) !== null) {
    const name = stripQuotes(m[3]!);
    const schema = m[2] ? stripQuotes(m[2]) : null;
    const kind = m[1]!.toUpperCase() === 'FUNCTION' ? 'function' : 'procedure';
    const key = `${(schema ?? '').toLowerCase()}.${name.toLowerCase()}|${kind}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ schema, name, kind });
  }
  return out;
}

/** One type created earlier in the same buffer. */
export type InBufferType = {
  readonly schema: string | null;
  readonly name: string;
  readonly kind: 'domain' | 'enum' | 'range' | 'composite' | 'table' | 'varray';
};

// What follows the type NAME tells the kind apart. Checked in order; the
// keyword forms all need a word after `AS`/`IS`, so the bare `AS (` composite
// form can safely come last. Oracle spells the introducer `IS` as often as
// `AS`; T-SQL's alias type (`CREATE TYPE dbo.ssn FROM varchar(11)`) is PG's
// DOMAIN under another name, so it is reported as one.
const CREATE_TYPE_TAIL_KINDS: ReadonlyArray<readonly [RegExp, InBufferType['kind']]> = [
  [/^\s*(?:AS|IS)\s+ENUM\b/i, 'enum'],
  [/^\s*(?:AS|IS)\s+RANGE\b/i, 'range'],
  [/^\s*(?:AS|IS)\s+TABLE\b/i, 'table'],
  [/^\s*(?:AS|IS)\s+VARRAY\b/i, 'varray'],
  [/^\s*(?:AS|IS)\s+OBJECT\b/i, 'composite'],
  [/^\s*(?:AS|IS)\s*\(/i, 'composite'],
  [/^\s*FROM\b/i, 'domain'],
];

/**
 * Types whose `CREATE [OR REPLACE] DOMAIN|TYPE` statement lives earlier in the
 * buffer, before the caret.
 *
 * Same rationale as `scanBufferCreatedRoutines` / `scanBufferCreatedMatviews`:
 * a script that declares a domain / enum / range type and then uses it as a
 * column type a few lines down is the normal authoring flow, and the schema
 * snapshot cannot know the type until the DDL has actually run — so
 * `price public.▮` listed every OTHER type in the schema but not the two the
 * script had just written (pgsql script.39 §5/§6, script.40 §4/§5).
 *
 * Buffer-wide up to the caret and comment-blind, exactly like
 * `scanBufferCreatedRoutines`: no attempt is made to skip a commented-out
 * `CREATE TYPE` or to replay intervening DROPs, and — deliberately — none to
 * skip STRING content either, because PG's own idempotent-DDL idiom wraps the
 * statement in a `DO $$ … $$` dollar-quoted block (which is exactly the shape
 * both coverage scripts use).
 *
 * Cross-engine by construction: the tail classifier covers Oracle's
 * `AS OBJECT` / `AS VARRAY(n) OF` / `AS TABLE OF` and T-SQL's `AS TABLE (…)` /
 * `FROM <base>` alongside the PG forms.
 * TODO(parity): mssql keeps its own richer, token-based
 * `scanMssqlBufferTableTypes` (mssqlExtras.ts) because its callers need the
 * TVP's COLUMN list to bind `FROM @var` relations, and oracle projects
 * `snapshotUserTypes` down to bare names with no schema — neither can adopt
 * this scanner without reshaping those call sites, so they are left as they
 * are (mssql script.43 §4/§10 and oracle script.40 §3/§4 both pass today).
 */
export function scanBufferCreatedTypes(
  text: string,
  cursorOffset: number,
): readonly InBufferType[] {
  const before = text.slice(0, cursorOffset);
  const re = new RegExp(
    `\\bCREATE\\s+(?:OR\\s+(?:ALTER|REPLACE)\\s+)?(DOMAIN|TYPE)\\s+` +
      `(?:(${IDENT})\\s*\\.\\s*)?(${IDENT})`,
    'gi',
  );
  const out: InBufferType[] = [];
  const seen = new Set<string>();
  let m: RegExpExecArray | null = null;
  while ((m = re.exec(before)) !== null) {
    const head = m[1]!.toUpperCase();
    const name = stripQuotes(m[3]!);
    // Oracle re-opens an existing type with `CREATE OR REPLACE TYPE BODY <n>`.
    // The spec that NAMED the type was scanned at its own `CREATE`, so the
    // body adds nothing — and what this match calls the name is the word
    // `BODY`.
    if (head === 'TYPE' && !m[2] && name.toUpperCase() === 'BODY') continue;
    const schema = m[2] ? stripQuotes(m[2]) : null;
    const tailStart = m.index + m[0].length;
    const kind: InBufferType['kind'] =
      head === 'DOMAIN'
        ? 'domain'
        : classifyCreateTypeTail(before.slice(tailStart, tailStart + 48));
    const key = `${(schema ?? '').toLowerCase()}.${name.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ schema, name, kind });
  }
  return out;
}

/** A bare `CREATE TYPE name;` declares a SHELL type — a composite placeholder
 *  filled in later — so an unrecognised tail falls back to `composite`. */
function classifyCreateTypeTail(tail: string): InBufferType['kind'] {
  for (const [re, kind] of CREATE_TYPE_TAIL_KINDS) {
    if (re.test(tail)) return kind;
  }
  return 'composite';
}

/** Strip one layer of identifier quoting, preserving case. */
function stripQuotes(raw: string): string {
  const first = raw[0];
  if (first === '"') return raw.slice(1, -1).replace(/""/g, '"');
  if (first === '[') return raw.slice(1, -1);
  if (first === '`') return raw.slice(1, -1).replace(/``/g, '`');
  return raw;
}

